import { cn } from "@/lib/utils";
import React from "react";
import { useTranslation } from "react-i18next";
import { statusToBadgeColor } from "@/lib/utils/statusTobadgeColor";
import { ExportStatus } from "@/lib/utils/exportStatus";
import { TFunction } from "i18next";
import { Option } from "../types/option";

export type BadgeProps = {
  color:
    | "error"
    | "quantilia_analysis"
    | "moi_validation"
    | "validated"
    | "ignored"
    | "pending"
    | "gp_production";
  custom_label?: string;
};

type ColorStyle = {
  bg: string;
  dot: string;
  labelKey: string;
};

const colorStyles: Record<BadgeProps["color"], ColorStyle> = {
  error: {
    bg: "bg-red-100",
    dot: "bg-red-500",
    labelKey: "badge.error",
  },
  quantilia_analysis: {
    bg: "bg-cyan-100",
    dot: "bg-cyan-500",
    labelKey: "badge.quantilia_analysis",
  },
  moi_validation: {
    bg: "bg-blue-100",
    dot: "bg-blue-500",
    labelKey: "badge.moi_validation",
  },
  validated: {
    bg: "bg-green-100",
    dot: "bg-green-500",
    labelKey: "badge.validated",
  },
  ignored: {
    bg: "bg-cyan-100",
    dot: "bg-cyan-500",
    labelKey: "badge.ignored",
  },
  pending: {
    bg: "bg-yellow-100",
    dot: "bg-yellow-500",
    labelKey: "badge.pending",
  },
  gp_production: {
    bg: "bg-yellow-100",
    dot: "bg-yellow-500",
    labelKey: "badge.gp_production",
  },
};

export const exportStatusToLabel = (
  status: ExportStatus,
  t: TFunction,
): string => {
  const color: BadgeProps["color"] = statusToBadgeColor[status];
  return t(colorStyles[color].labelKey);
};

export const exportStatusLabels = (t: TFunction): Array<Option> => {
  const uniqueOptions = new Map<string, Option>();
  const statuses = Object.values(ExportStatus) as ExportStatus[];
  for (const status of statuses) {
    const label = exportStatusToLabel(status, t);
    const existing = uniqueOptions.get(label);
    if (!existing) {
      uniqueOptions.set(label, {
        label: label,
        value: status,
      });
    } else {
      const oldValue: string = existing.value;
      uniqueOptions.set(label, {
        label: label,
        value: oldValue + "," + status,
      });
    }
  }
  return Array.from(uniqueOptions.values());
};

export const Badge: React.FC<BadgeProps> = ({ color }) => {
  const { t } = useTranslation();

  const style = colorStyles[color];
  if (!style) {
    return null;
  }

  const { bg, dot, labelKey } = style;

  return (
    <div className="flex flex-row gap-x-3">
      <div className={cn(bg, "rounded-full box-border shadow h-3.5 w-3.5 p-1")}>
        <div className={cn(dot, "rounded-full h-1.5 w-1.5")} />
      </div>
      <span>{t(labelKey)}</span>
    </div>
  );
};
